/**
 * BuzzTalk Zustand store — single source of truth for chat UI state.
 *
 * - Bootstraps the socket on mount and forwards events into state.
 * - Loads groups via REST on init; loads messages on demand per group.
 * - Reconnect hydration: on socket `connect`, the selected group refetches
 *   messages since its `lastReadAt` so missed messages backfill.
 */

import { create } from 'zustand';
import { chatApi } from '../services/chat/chatApi';
import type {
  ChatGroup,
  ChatMessage,
  ChatTask,
  ChatGroupMember,
  ChatNotifyMode,
  UploadedAttachment,
  GuestInviteCreated,
} from '../services/chat/chatApi';
import {
  subscribeToGroup,
  unsubscribeFromGroup,
  unsubscribeAll,
  authenticateRealtime,
  clearRealtimeAuth,
} from '../services/chat/realtimeClient';

interface ChatState {
  // ---- data ----
  groups: ChatGroup[];
  messagesByGroup: Record<string, ChatMessage[]>;
  membersByGroup: Record<string, ChatGroupMember[]>;
  tasksByGroup: Record<string, ChatTask[]>;
  typingByGroup: Record<string, Set<string>>;
  selectedGroupId: string | null;
  unreadByGroup: Record<string, number>;
  totalUnread: number;
  isInitialized: boolean;
  isLoadingMessages: boolean;
  notificationsBadge: number;
  /** Set when the REST bootstrap failed, so the surface can say so and retry. */
  loadError: string | null;

  // ---- actions ----
  init: () => Promise<void>;
  teardown: () => void;
  selectGroup: (groupId: string | null) => Promise<void>;
  refreshGroups: () => Promise<void>;
  loadMessages: (groupId: string, opts?: { since?: string }) => Promise<void>;
  sendMessage: (
    groupId: string,
    body: string,
    opts?: { replyToId?: string; attachments?: UploadedAttachment[]; metadata?: Record<string, unknown> },
  ) => Promise<ChatMessage>;
  editMessage: (messageId: string, body: string) => Promise<void>;
  deleteMessage: (messageId: string) => Promise<void>;
  toggleReaction: (messageId: string, emoji: string) => Promise<void>;
  markRead: (groupId: string) => Promise<void>;
  loadMembers: (groupId: string) => Promise<void>;
  addMemberToGroup: (groupId: string, userId: string) => Promise<void>;
  removeMemberFromGroup: (groupId: string, userId: string) => Promise<void>;
  loadTasks: (groupId: string) => Promise<void>;
  createTask: (
    groupId: string,
    body: { title: string; description?: string; assigneeId?: string; dueAt?: string },
  ) => Promise<ChatTask>;
  promoteToTask: (messageId: string) => Promise<ChatTask>;
  updateTaskStatus: (taskId: string, status: 'TODO' | 'DOING' | 'DONE') => Promise<void>;
  deleteTask: (taskId: string) => Promise<void>;
  createGroup: (input: {
    name: string;
    description?: string;
    type?: 'TEAM' | 'PROJECT' | 'TOPIC' | 'CLIENT';
    memberIds?: string[];
  }) => Promise<ChatGroup>;
  setNotifyMode: (groupId: string, mode: ChatNotifyMode) => Promise<void>;
  inviteGuest: (groupId: string, email: string) => Promise<GuestInviteCreated>;
  sendTyping: (groupId: string, start: boolean) => void;
  uploadAttachment: (file: File) => Promise<UploadedAttachment>;
}

const initialState: Omit<
  ChatState,
  | 'init'
  | 'teardown'
  | 'selectGroup'
  | 'refreshGroups'
  | 'loadMessages'
  | 'sendMessage'
  | 'editMessage'
  | 'deleteMessage'
  | 'toggleReaction'
  | 'markRead'
  | 'loadMembers'
  | 'addMemberToGroup'
  | 'removeMemberFromGroup'
  | 'loadTasks'
  | 'createTask'
  | 'promoteToTask'
  | 'updateTaskStatus'
  | 'deleteTask'
  | 'createGroup'
  | 'setNotifyMode'
  | 'inviteGuest'
  | 'sendTyping'
  | 'uploadAttachment'
> = {
  groups: [],
  messagesByGroup: {},
  membersByGroup: {},
  tasksByGroup: {},
  typingByGroup: {},
  selectedGroupId: null,
  unreadByGroup: {},
  totalUnread: 0,
  isInitialized: false,
  isLoadingMessages: false,
  notificationsBadge: 0,
  loadError: null,
};

// Stable empty defaults — referenced by component selectors that look up
// per-group collections (e.g. `messagesByGroup[id]`). Returning the same
// empty array/set/record on every render is what keeps React from hitting
// the infinite-render loop (#185) on first paint before data arrives.
export const EMPTY_MESSAGES: readonly ChatMessage[] = Object.freeze([]);
export const EMPTY_TASKS: readonly ChatTask[] = Object.freeze([]);
export const EMPTY_MEMBERS: readonly ChatGroupMember[] = Object.freeze([]);
const EMPTY_TYPING: ReadonlySet<string> = new Set();

// Typed selector helpers so component code stays one-line + always references
// the stable empty defaults above.
export const selectMessages = (groupId: string) =>
  (s: ChatState): readonly ChatMessage[] =>
    s.messagesByGroup[groupId] ?? EMPTY_MESSAGES;

export const selectTasks = (groupId: string) =>
  (s: ChatState): readonly ChatTask[] =>
    s.tasksByGroup[groupId] ?? EMPTY_TASKS;

export const selectMembers = (groupId: string) =>
  (s: ChatState): readonly ChatGroupMember[] =>
    s.membersByGroup[groupId] ?? EMPTY_MEMBERS;

export const useChatStore = create<ChatState>((set, get) => ({
  ...initialState,

  init: async () => {
    if (get().isInitialized) return;
    // Authenticate Realtime BEFORE subscribing so RLS policies that reference
    // auth.uid() see the BuzzBit user id, not anon. Failure is non-fatal —
    // the UI still works (REST + optimistic updates), but live cross-user
    // broadcast won't fire until the server SUPABASE_JWT_SECRET is configured.
    await authenticateRealtime().catch(() => false);
    // The REST bootstrap needs the same containment. Callers invoke this as
    // `void init()` from an effect, and `void` suppresses the lint without
    // catching anything — so a 429, a 500 or a dropped connection escaped as an
    // unhandled rejection and reached window.onerror, leaving the surface
    // permanently empty with nothing said. Record it and stay uninitialised so
    // a remount retries.
    try {
      await get().refreshGroups();
    } catch (err) {
      set({ loadError: err instanceof Error ? err.message : String(err) });
      return;
    }
    set({ isInitialized: true, loadError: null });
  },

  teardown: () => {
    unsubscribeAll();
    clearRealtimeAuth();
    set({ ...initialState });
  },

  refreshGroups: async () => {
    const groups = await chatApi.listGroups({ limit: 100 });
    const unread: Record<string, number> = {};
    for (const g of groups) unread[g.id] = g.unreadCount ?? 0;
    set({
      groups,
      unreadByGroup: unread,
      totalUnread: Object.values(unread).reduce((a, b) => a + b, 0),
    });
  },

  selectGroup: async (groupId) => {
    const prevGroupId = get().selectedGroupId;
    set({ selectedGroupId: groupId });
    if (prevGroupId && prevGroupId !== groupId) {
      unsubscribeFromGroup(prevGroupId);
    }
    if (!groupId) return;
    set({ isLoadingMessages: true });
    // Same containment as init(): the callers are `void selectGroup(...)`, so
    // anything thrown here would escape unhandled. A thread that fails to load
    // renders empty with the notice, and the group stays selected so Retry
    // targets it.
    try {
      await Promise.all([
        get().loadMessages(groupId),
        get().loadMembers(groupId),
        get().loadTasks(groupId),
      ]);
      await get().markRead(groupId);
      set({ loadError: null });
    } catch (err) {
      set({ loadError: err instanceof Error ? err.message : String(err) });
      return;
    } finally {
      set({ isLoadingMessages: false });
    }
    // Live: Realtime postgres_changes for this group only.
    subscribeToGroup(groupId, {
      onMessage: (evt) => {
        chatApi
          .listMessages(evt.groupId, {
            limit: 1,
            since: new Date(Date.now() - 5_000).toISOString(),
          })
          .then((latest) => {
            const match = latest.find((m) => m.id === evt.messageId);
            if (!match) return;
            set((s) => {
              const existing = s.messagesByGroup[evt.groupId] ?? [];
              if (existing.some((m) => m.id === evt.messageId)) return s;
              const next = [...existing, match].sort((a, b) =>
                a.createdAt.localeCompare(b.createdAt),
              );
              return {
                messagesByGroup: { ...s.messagesByGroup, [evt.groupId]: next },
                groups: s.groups.map((g) =>
                  g.id === evt.groupId ? { ...g, lastMessageAt: match.createdAt } : g,
                ),
              };
            });
          })
          .catch(() => undefined);
      },
      onReaction: (evt) => {
        set((s) => {
          const next: Record<string, ChatMessage[]> = {};
          for (const [gid, list] of Object.entries(s.messagesByGroup)) {
            next[gid] = list.map((m) => {
              if (m.id !== evt.messageId) return m;
              const reactions = (m.reactions ?? []).filter(
                (r) => !(r.userId === evt.userId && r.emoji === evt.emoji),
              );
              if (evt.action === 'add') reactions.push({ userId: evt.userId, emoji: evt.emoji });
              return { ...m, reactions };
            });
          }
          return { messagesByGroup: next };
        });
      },
      onTask: (evt) => {
        chatApi
          .listTasks(evt.groupId)
          .then((list) =>
            set((s) => ({ tasksByGroup: { ...s.tasksByGroup, [evt.groupId]: list } })),
          )
          .catch(() => undefined);
      },
    });
  },

  loadMessages: async (groupId, opts) => {
    const list = await chatApi.listMessages(groupId, { limit: 50, since: opts?.since });
    set((s) => {
      const existing = s.messagesByGroup[groupId] ?? [];
      let next: ChatMessage[];
      if (opts?.since) {
        // Incremental — append newer ones, dedupe by id, sort ascending
        const seen = new Set(existing.map((m) => m.id));
        const added = list.filter((m) => !seen.has(m.id));
        next = [...existing, ...added].sort((a, b) => a.createdAt.localeCompare(b.createdAt));
      } else {
        // Initial fetch — REST returns newest-first; flip to ascending for UI.
        next = [...list].sort((a, b) => a.createdAt.localeCompare(b.createdAt));
      }
      return { messagesByGroup: { ...s.messagesByGroup, [groupId]: next } };
    });
  },

  sendMessage: async (groupId, body, opts) => {
    const message = await chatApi.sendMessage(groupId, {
      body,
      replyToId: opts?.replyToId,
      attachments: opts?.attachments,
      metadata: opts?.metadata,
    });
    set((s) => {
      const existing = s.messagesByGroup[groupId] ?? [];
      if (existing.some((m) => m.id === message.id)) return s;
      return { messagesByGroup: { ...s.messagesByGroup, [groupId]: [...existing, message] } };
    });
    // Update lastMessageAt on the group preview
    set((s) => ({
      groups: s.groups.map((g) =>
        g.id === groupId ? { ...g, lastMessageAt: message.createdAt } : g,
      ),
    }));
    return message;
  },

  editMessage: async (messageId, body) => {
    const updated = await chatApi.editMessage(messageId, body);
    set((s) => {
      const list = s.messagesByGroup[updated.groupId];
      if (!list) return s;
      return {
        messagesByGroup: {
          ...s.messagesByGroup,
          [updated.groupId]: list.map((m) =>
            m.id === messageId
              ? { ...m, body: updated.body, editedAt: updated.editedAt, mentions: updated.mentions }
              : m,
          ),
        },
      };
    });
  },

  deleteMessage: async (messageId) => {
    await chatApi.deleteMessage(messageId);
    set((s) => {
      const next = { ...s.messagesByGroup };
      for (const [gid, list] of Object.entries(next)) {
        next[gid] = list.map((m) =>
          m.id === messageId ? { ...m, body: '[deleted]', deletedAt: new Date().toISOString(), mentions: [] } : m,
        );
      }
      return { messagesByGroup: next };
    });
  },

  toggleReaction: async (messageId, emoji) => {
    await chatApi.toggleReaction(messageId, emoji);
    // The server emits reaction:change; the listener applies the delta. No optimistic update.
  },

  markRead: async (groupId) => {
    await chatApi.markRead(groupId).catch(() => undefined);
    set((s) => {
      const next = { ...s.unreadByGroup };
      next[groupId] = 0;
      const total = Object.values(next).reduce((a, b) => a + b, 0);
      return { unreadByGroup: next, totalUnread: total };
    });
  },

  loadMembers: async (groupId) => {
    const list = await chatApi.listMembers(groupId);
    set((s) => ({ membersByGroup: { ...s.membersByGroup, [groupId]: list } }));
  },

  addMemberToGroup: async (groupId, userId) => {
    await chatApi.addMember(groupId, userId);
    // Refetch so the new row carries its real ChatGroupMember.id / role / user
    // relation instead of an optimistic stub.
    const list = await chatApi.listMembers(groupId);
    set((s) => ({
      membersByGroup: { ...s.membersByGroup, [groupId]: list },
      groups: s.groups.map((g) =>
        g.id === groupId ? { ...g, updatedAt: new Date().toISOString() } : g,
      ),
    }));
  },

  removeMemberFromGroup: async (groupId, userId) => {
    await chatApi.removeMember(groupId, userId);
    set((s) => ({
      membersByGroup: {
        ...s.membersByGroup,
        [groupId]: (s.membersByGroup[groupId] ?? []).filter((m) => m.userId !== userId),
      },
    }));
  },

  loadTasks: async (groupId) => {
    const list = await chatApi.listTasks(groupId);
    set((s) => ({ tasksByGroup: { ...s.tasksByGroup, [groupId]: list } }));
  },

  createTask: async (groupId, body) => {
    const task = await chatApi.createTask(groupId, body);
    set((s) => ({
      tasksByGroup: { ...s.tasksByGroup, [groupId]: [...(s.tasksByGroup[groupId] ?? []), task] },
    }));
    return task;
  },

  promoteToTask: async (messageId) => {
    const task = await chatApi.promoteToTask(messageId);
    set((s) => ({
      tasksByGroup: {
        ...s.tasksByGroup,
        [task.groupId]: [...(s.tasksByGroup[task.groupId] ?? []), task],
      },
    }));
    return task;
  },

  updateTaskStatus: async (taskId, status) => {
    const updated = await chatApi.updateTask(taskId, { status });
    set((s) => {
      const list = s.tasksByGroup[updated.groupId] ?? [];
      return {
        tasksByGroup: {
          ...s.tasksByGroup,
          [updated.groupId]: list.map((t) => (t.id === taskId ? { ...t, ...updated } : t)),
        },
      };
    });
  },

  deleteTask: async (taskId) => {
    await chatApi.deleteTask(taskId);
    set((s) => {
      const next: Record<string, ChatTask[]> = {};
      for (const [gid, list] of Object.entries(s.tasksByGroup)) {
        next[gid] = list.filter((t) => t.id !== taskId);
      }
      return { tasksByGroup: next };
    });
  },

  createGroup: async (input) => {
    const group = await chatApi.createGroup(input);
    set((s) => ({ groups: [group, ...s.groups] }));
    return group;
  },

  setNotifyMode: async (groupId, mode) => {
    await chatApi.setNotifyMode(groupId, mode);
    set((s) => ({
      membersByGroup: {
        ...s.membersByGroup,
        [groupId]: (s.membersByGroup[groupId] ?? []).map((m) =>
          m.userId === (s.membersByGroup[groupId] ?? []).find(() => true)?.userId
            ? { ...m, notifyMode: mode }
            : m,
        ),
      },
    }));
  },

  inviteGuest: async (groupId, email) => chatApi.inviteGuest(groupId, email),

  sendTyping: (_groupId, _start) => {
    // Typing indicators removed with Socket.io. v2 can use Supabase Realtime
    // presence channel state if/when desired.
    void _groupId;
    void _start;
  },

  uploadAttachment: async (file) => {
    return chatApi.upload(file);
  },
}));

// Stable selector for "users currently typing in this group". Returns the
// same empty-set reference when no entry exists — prevents React #185
// (infinite render loop) caused by `s.typingByGroup[id] ?? new Set()`
// returning a fresh Set every render.
export function selectTypingInGroup(groupId: string) {
  return (s: ChatState): ReadonlySet<string> =>
    (s.typingByGroup[groupId] as ReadonlySet<string> | undefined) ?? EMPTY_TYPING;
}

export default useChatStore;
